import type { Units } from '@/stores/prefsStore';

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** "just now", "4 min ago", "3 h ago", "2 days ago". */
export function timeAgo(t: number, now: number): string {
  const diff = Math.max(0, now - t);
  if (diff < MIN) return 'just now';
  if (diff < HOUR) return `${Math.floor(diff / MIN)} min ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} h ago`;
  const days = Math.round(diff / DAY);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

const clockFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const dayFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const weekdayFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const dateFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/** "8:42 AM" (locale-aware). */
export const formatClock = (t: number) => clockFmt.format(t);
/** "Tue, Nov 3". */
export const formatDay = (t: number) => dayFmt.format(t);
/** "Tue 3". */
export const formatShortDay = (t: number) => `${weekdayFmt.format(t)} ${new Date(t).getDate()}`;
/** "Nov 3, 2026". */
export const formatDate = (t: number) => dateFmt.format(t);

/** "Today 8:42 AM", "Yesterday 6:10 PM", "Tue, Nov 3 9:00 AM". */
export function formatWhen(t: number, now: number): string {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const today = start.getTime();
  if (t >= today) return `Today ${formatClock(t)}`;
  start.setDate(start.getDate() - 1);
  if (t >= start.getTime()) return `Yesterday ${formatClock(t)}`;
  return `${formatDay(t)} ${formatClock(t)}`;
}

/** "42 min", "2 h 5 min", "3 days". */
export function formatDuration(ms: number): string {
  const mins = Math.max(0, Math.round(ms / MIN));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 48) return m ? `${h} h ${m} min` : `${h} h`;
  return `${Math.round(h / 24)} days`;
}

/** Distance in the user's units: "320 m", "4.2 km", "0.4 mi", "12 mi". */
export function formatDistance(meters: number, units: Units): string {
  if (units === 'km') {
    if (meters < 1000) return `${Math.round(meters / 10) * 10} m`;
    const km = meters / 1000;
    return `${km < 10 ? km.toFixed(1) : Math.round(km)} km`;
  }
  const mi = meters / 1609.344;
  if (mi < 0.1) return `${Math.round((meters * 3.28084) / 10) * 10} ft`;
  return `${mi < 10 ? mi.toFixed(1) : Math.round(mi)} mi`;
}

/** Short radius label: "75 m" / "250 ft". */
export function formatRadius(meters: number, units: Units): string {
  if (units === 'km') return `${meters} m`;
  return `${Math.round((meters * 3.28084) / 5) * 5} ft`;
}

/** "37.75021, -122.43372". */
export function formatCoords(lat: number, lng: number, digits = 5): string {
  return `${lat.toFixed(digits)}, ${lng.toFixed(digits)}`;
}

/** "1,284". */
export const formatNumber = (n: number) => n.toLocaleString();

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}
